import { useState, useRef, useCallback, useEffect } from "react";
import { useRoute, useLocation } from "wouter";
import { useAuth } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import AdminLayout from "./AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Send, Eye, EyeOff, ImagePlus, Loader2 } from "lucide-react";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";

interface MemberRow {
  id: number;
  email: string;
  displayName: string | null;
  membershipTier: string;
}

export default function AdminEmailComposePage() {
  const [, params] = useRoute("/admin/members/:id/email");
  const [, navigate] = useLocation();
  const { getToken } = useAuth();
  const { toast } = useToast();

  const memberId = parseInt(params?.id ?? "0", 10);

  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [insertingImg, setInsertingImg] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const { data: member, isLoading } = useQuery<MemberRow>({
    queryKey: ["admin-member", memberId],
    enabled: !!memberId,
    queryFn: async () => {
      const token = await getToken();
      const res = await fetch("/api/admin/members", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("載入失敗");
      const all: MemberRow[] = await res.json();
      const found = all.find(m => m.id === memberId);
      if (!found) throw new Error("找不到會員");
      return found;
    },
  });

  const recipientEmail = member?.email ?? "";

  // Upload image file to API server → GCS, get back a real HTTPS URL
  async function uploadImageAndGetUrl(file: File): Promise<string> {
    const token = await getToken();
    const formData = new FormData();
    formData.append("image", file);
    const res = await fetch("/api/admin/upload-image", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error ?? "上傳失敗");
    return data.url as string;
  }

  function insertImgTag(url: string) {
    const ta = textareaRef.current;
    const imgTag = `<img src="${url}" alt="" style="max-width:100%;height:auto">`;
    if (ta) {
      const start = ta.selectionStart ?? html.length;
      const end = ta.selectionEnd ?? html.length;
      const newVal = html.slice(0, start) + imgTag + html.slice(end);
      setHtml(newVal);
      requestAnimationFrame(() => {
        ta.focus();
        ta.selectionStart = ta.selectionEnd = start + imgTag.length;
      });
    } else {
      setHtml(prev => prev + imgTag);
    }
  }

  const handlePaste = useCallback(async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = Array.from(e.clipboardData.items);
    const imgItem = items.find(item => item.type.startsWith("image/"));
    if (!imgItem) return;
    e.preventDefault();
    setInsertingImg(true);
    try {
      const file = imgItem.getAsFile();
      if (!file) return;
      const url = await uploadImageAndGetUrl(file);
      insertImgTag(url);
    } catch (err: any) {
      toast({ title: "圖片上傳失敗", description: err.message, variant: "destructive" });
    } finally {
      setInsertingImg(false);
    }
  }, [html, getToken]);

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setInsertingImg(true);
    try {
      const url = await uploadImageAndGetUrl(file);
      insertImgTag(url);
    } catch (err: any) {
      toast({ title: "圖片上傳失敗", description: err.message, variant: "destructive" });
    } finally {
      setInsertingImg(false);
      e.target.value = "";
    }
  }

  function handleSend() {
    if (!subject.trim()) { toast({ title: "請填寫主題", variant: "destructive" }); return; }
    if (!html.trim()) { toast({ title: "請填寫內文", variant: "destructive" }); return; }
    setConfirmOpen(true);
  }

  async function doSend() {
    setConfirmOpen(false);
    setSending(true);
    try {
      const token = await getToken();
      const res = await fetch(`/api/admin/members/${memberId}/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ subject, html }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "發送失敗");
      toast({ title: "電郵已發送", description: `已成功發送至 ${data.to}` });
      navigate("/admin/members");
    } catch (e: any) {
      toast({ title: "發送失敗", description: e.message, variant: "destructive" });
    } finally {
      setSending(false);
    }
  }

  return (
    <AdminLayout tab="members">
      <div className="max-w-2xl space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => navigate("/admin/members")}>
            <ArrowLeft className="h-4 w-4" /> 返回會員列表
          </Button>
        </div>

        <div>
          <h2 className="text-lg font-semibold">發送電郵</h2>
          <p className="text-sm text-muted-foreground mt-0.5">獨立發送電郵給指定會員</p>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">收件人</Label>
              <div className="flex items-center gap-2 px-3 py-2 rounded-md border bg-secondary/40 text-sm">
                <span className="font-medium">{member?.displayName ?? member?.email}</span>
                {member?.displayName && (
                  <span className="text-muted-foreground">({recipientEmail})</span>
                )}
                {!member?.displayName && (
                  <span className="text-muted-foreground">{member?.membershipTier === "paid" ? "付費會員" : "免費會員"}</span>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="subject" className="text-sm font-medium">主題</Label>
              <Input
                id="subject"
                placeholder="電郵主題…"
                value={subject}
                onChange={e => setSubject(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="html" className="text-sm font-medium">內文（HTML）</Label>
                <div className="flex items-center gap-1">
                  {!showPreview && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 gap-1.5 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      disabled={insertingImg}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {insertingImg
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <ImagePlus className="h-3.5 w-3.5" />}
                      {insertingImg ? "插入中…" : "插入圖片"}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1.5 text-xs text-muted-foreground"
                    onClick={() => setShowPreview(p => !p)}
                  >
                    {showPreview ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    {showPreview ? "原始碼" : "預覽"}
                  </Button>
                </div>
              </div>

              {showPreview ? (
                <div
                  className="min-h-[280px] rounded-md border p-4 text-sm overflow-auto bg-white"
                  dangerouslySetInnerHTML={{ __html: html }}
                />
              ) : (
                <Textarea
                  id="html"
                  ref={textareaRef}
                  placeholder="<p>您好，…</p>&#10;&#10;可直接 Ctrl+V 貼上圖片，或點擊「插入圖片」按鈕選擇圖片檔案。"
                  value={html}
                  onChange={e => setHtml(e.target.value)}
                  onPaste={handlePaste}
                  className="min-h-[280px] font-mono text-sm resize-y"
                />
              )}

              <p className="text-xs text-muted-foreground">
                支援 HTML 格式。插入圖片：直接 <kbd className="px-1 py-0.5 rounded bg-muted border text-[10px]">Ctrl+V</kbd> 貼上，或點擊「插入圖片」選擇檔案。圖片以 base64 嵌入，無需外部連結。
              </p>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileSelect}
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button onClick={handleSend} disabled={sending} className="gap-2">
                <Send className="h-4 w-4" />
                {sending ? "發送中…" : "發送電郵"}
              </Button>
              <Button variant="outline" onClick={() => navigate("/admin/members")}>
                取消
              </Button>
            </div>
          </div>
        )}
      </div>
      <ConfirmDialog
        open={confirmOpen}
        title="確定發送電郵？"
        description={`確定發送電郵給 ${recipientEmail}？`}
        confirmLabel="確定發送"
        onConfirm={doSend}
        onCancel={() => setConfirmOpen(false)}
      />
    </AdminLayout>
  );
}
